import { pathnameWithoutLocalePrefix } from '@susu/locale-utils'

export type PageType =
  | 'account'
  | 'black_tie_configurator'
  | 'cart'
  | 'category_landing'
  | 'category'
  | 'checkout'
  | 'custommade'
  | 'error'
  | 'giftcard'
  | 'home'
  | 'journal'
  | 'login'
  | 'lookbuilder'
  | 'product'
  | 'return'
  | 'store'
  | 'support'
  | 'wishlist'
  | 'booking_tool'

// Ordered: first match wins, so specific prefixes (e.g. /men/black-tie-package)
// must stay ahead of broader ones (e.g. the /men/ category prefix).
const pageTypesByPrefix: Array<[PageType, string[]]> = [
  ['error', ['/error', '/404', '/500']],
  ['support', ['/support', '/help', '/contact']],
  ['account', ['/account', '/profile', '/my-account']],
  ['login', ['/login', '/signin', '/register', '/signup']],
  ['wishlist', ['/wishlist', '/favorites']],
  ['cart', ['/cart', '/bag']],
  ['checkout', ['/checkout', '/payment', '/billing']],
  ['giftcard', ['/giftcard', '/gift-card', '/gift']],
  ['store', ['/stores', '/store/', '/find-store']],
  ['journal', ['/journal', '/blog', '/content']],
  ['black_tie_configurator', ['/men/black-tie-package', '/black-tie-package']],
  ['custommade', ['/custom-made', '/custommade', '/configurator']],
  ['lookbuilder', ['/lookbuilder', '/look-builder', '/outfit-builder']],
]

const skuPatterns = [
  /\/[A-Z0-9]+\.html$/, // SKU pattern like /H7404.html
  /\/[A-Z0-9]+-[A-Z0-9]+\.html$/, // SKU with variant like /C7334-S.html
  /\/[a-z0-9-]+\/[A-Z0-9]+\.html$/, // Product path with SKU
]

const categoryLandingPrefixes = [
  '/discover-all-suits',
  '/shop-tuxedo-collection',
]

const categoryPrefixes = [
  '/new-arrivals',
  '/best-sellers',
  '/sale',
  '/clearance',
  '/men/',
  '/women/',
  '/accessories/',
  '/suits/',
  '/shirts/',
  '/trousers/',
  '/shoes/',
  '/coats/',
  '/jackets/',
  '/knitwear/',
  '/ties/',
  '/belts/',
  '/socks/',
  '/handkerchiefs/',
  '/cufflinks/',
]

export const getPageType = (): PageType => {
  // Guard statement for SSR/backend/test environments where window is not defined
  if (typeof window === 'undefined') {
    return 'home'
  }

  const pathname = pathnameWithoutLocalePrefix(window.location?.pathname || '')

  // Appointment booking pages (subdomain)
  if (window.location.hostname.includes('appointment.suitsupply.com')) {
    return 'booking_tool'
  }

  const prefixed = pageTypesByPrefix.find(([, prefixes]) =>
    prefixes.some(prefix => pathname.startsWith(prefix)),
  )
  if (prefixed) {
    return prefixed[0]
  }

  if (skuPatterns.some(pattern => pattern.test(pathname))) {
    return 'product'
  }

  if (categoryLandingPrefixes.some(prefix => pathname.startsWith(prefix))) {
    return 'category_landing'
  }

  if (categoryPrefixes.some(prefix => pathname.startsWith(prefix))) {
    return 'category'
  }

  if (pathname.startsWith('/returns')) {
    return 'return'
  }

  return 'home'
}
